import { useState, useRef } from 'react';
import { Upload, Trash2, Loader2 } from 'lucide-react';
import { useFonts } from '../../contexts/FontContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';

export default function FontsTab() {
  const { fonts, loading, addFont, removeFont } = useFonts();
  const { isWithinLimit } = useWorkspace();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canAddFont = isWithinLimit('fonts', fonts.length);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        // Derive font name and family from filename
        const baseName = file.name.replace(/\.(ttf|otf|woff2?)$/i, '');
        // Capitalize words for display name
        const displayName = baseName.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        // Use base name as family (with spaces instead of dashes/underscores)
        const family = baseName.replace(/[-_]/g, ' ');

        await addFont(file, displayName, family);
      }
    } catch (err) {
      console.error('[Fonts] Upload failed:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Remove this font?')) return;
    try {
      await removeFont(id);
    } catch (err) {
      console.error('[Fonts] Delete failed:', err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Fonts</h3>
        <div className="flex items-center gap-3">
          {uploading && <Loader2 size={16} className="animate-spin text-blue-500" />}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={!canAddFont || uploading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition"
          >
            <Upload size={16} />
            Upload Font
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".ttf,.otf,.woff,.woff2"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      </div>

      {!canAddFont && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-400">
          Font limit reached for your plan. Upgrade to add more fonts.
        </div>
      )}

      <p className="text-sm text-gray-500 dark:text-gray-400">
        Upload custom fonts (TTF, OTF, WOFF, WOFF2) for use in product templates. Fonts are stored securely in your workspace.
      </p>

      {/* Font List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
        </div>
      ) : fonts.length === 0 ? (
        <div className="text-center py-12 text-gray-400 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
          <Upload size={36} className="mx-auto mb-3 opacity-40" />
          <p>No custom fonts uploaded</p>
          <p className="text-sm mt-1">Upload TTF, OTF, or WOFF files to use in templates</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {fonts.map(font => (
            <div
              key={font.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex items-center justify-between"
            >
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 dark:text-white text-sm truncate">
                  {font.name}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {font.family}
                  {font.category && ` \u00b7 ${font.category}`}
                </p>
                {/* Font preview */}
                <p
                  className="text-lg text-gray-700 dark:text-gray-300 mt-2 truncate"
                  style={{ fontFamily: font.family }}
                >
                  The quick brown fox jumps
                </p>
              </div>
              <button
                onClick={() => handleDelete(font.id)}
                className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-red-500 transition flex-shrink-0 ml-3"
                title="Remove font"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
